export const QUOTE_ID_KEY = "liability_quote_id"

export function storeQuoteId(quoteId: string | undefined) {
  if (!quoteId || typeof quoteId !== "string") {
    return
  }
  sessionStorage.setItem(QUOTE_ID_KEY, quoteId)
}

export function getQuoteIdFromStorage(): string | undefined | null {
  return sessionStorage.getItem(QUOTE_ID_KEY)
}

export function removeQuoteIdFromStorage() {
  void sessionStorage.removeItem(QUOTE_ID_KEY)
}

const PREPACKAGE_LOADER_SEEN_KEY = "prepackage_loader_seen"

/**
 * Tracks the pets for which the pricing loader has already played,
 * scoped to the active quote. Keyed by `quoteId` so a new quote resets the state.
 */
type PrepackageLoaderSeen = {
  quoteId: string
  petIds: string[]
}

function getPrepackageLoaderSeen(): PrepackageLoaderSeen | null {
  if (typeof window === "undefined") {
    return null
  }
  try {
    const raw = sessionStorage.getItem(PREPACKAGE_LOADER_SEEN_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PrepackageLoaderSeen>
    if (typeof parsed?.quoteId !== "string" || !Array.isArray(parsed.petIds)) {
      return null
    }
    return { quoteId: parsed.quoteId, petIds: parsed.petIds }
  } catch {
    return null
  }
}

/** True when the loader has already played for this pet in the current quote. */
export function hasSeenPrepackageLoader(quoteId: string, petId: string): boolean {
  if (!quoteId || !petId) return false
  const seen = getPrepackageLoaderSeen()
  if (!seen || seen.quoteId !== quoteId) return false
  return seen.petIds.includes(petId)
}

/** Marks the loader as played for this pet. A different quoteId resets the state. */
export function markPrepackageLoaderSeen(quoteId: string, petId: string) {
  if (typeof window === "undefined" || !quoteId || !petId) {
    return
  }
  const seen = getPrepackageLoaderSeen()
  const next: PrepackageLoaderSeen =
    seen && seen.quoteId === quoteId
      ? { quoteId, petIds: seen.petIds.includes(petId) ? seen.petIds : [...seen.petIds, petId] }
      : { quoteId, petIds: [petId] }
  try {
    sessionStorage.setItem(PREPACKAGE_LOADER_SEEN_KEY, JSON.stringify(next))
  } catch {
    // Storage unavailable (private mode, quota): best-effort, the loader will
    // simply replay next time rather than crash.
  }
}
